import { useEffect, useRef, useState } from 'react';
import './App.css';
import ChatAdvisor, { clearTemplateChatHistory } from './ChatAdvisor';
import LessonIframe from './LessonIframe';
import CodePreview from './CodePreview';
import { injectNavGuard } from './navGuard';

const STORAGE_KEY = 'daily-huddle-board';
const LEGACY_STORAGE_KEY = 'daily-huddle-lesson';
const OPENAI_MODEL_ID = 'gpt-5.1';
const LEGACY_OPENAI_MODELS = ['gpt5', 'openai', 'gpt-5.1-codex', 'gpt-5.1-codex-max'];

function App() {
  const iframeRef = useRef(null);
  const lessonFrameRef = useRef(null);
  const menuButtonRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLessonLoading, setIsLessonLoading] = useState(true);
  const [lessonError, setLessonError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateImages, setNewTemplateImages] = useState([]);
  const [newTemplateImagePreviews, setNewTemplateImagePreviews] = useState([]);
  const [newTemplateFile, setNewTemplateFile] = useState(null);
  const [newTemplateFilePath, setNewTemplateFilePath] = useState(null);
  const [newTemplateModel, setNewTemplateModel] = useState(OPENAI_MODEL_ID); // Model selection for template creation
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [creatingTemplateName, setCreatingTemplateName] = useState('');
  const [creatingTemplateDescription, setCreatingTemplateDescription] = useState('');
  const templateImageInputRef = useRef(null);
  const templateFileInputRef = useRef(null);
  const createInFlightRef = useRef(false); // prevent double submit (e.g. double-click or Strict Mode)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1); // 1 = first confirmation, 2 = second confirmation
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyLessonName, setCopyLessonName] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState(true); // Default to gallery view on load
  const [chatDocked, setChatDocked] = useState(true);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(true);
  const [chatNudge, setChatNudge] = useState(false);
  const [chatTooltip, setChatTooltip] = useState(false);
  const [galleryNudge, setGalleryNudge] = useState(false);
  const [galleryTooltip, setGalleryTooltip] = useState(false);
  const [chatPosition, setChatPosition] = useState({ x: 720, y: 140 });
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const [iframeNonce, setIframeNonce] = useState(Date.now());
  const selectedTemplateRef = useRef(null); // track current selection for async create (allow opening other apps while creating)
  const [showDeployConfirmModal, setShowDeployConfirmModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [deploymentUrl, setDeploymentUrl] = useState(null);
  const [existingDeployment, setExistingDeployment] = useState(null);
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [apiKeysConfig, setApiKeysConfig] = useState({ providers_set: { google: false, openai: false, anthropic: false }, enabled_models: [], use_user_keys_only: false });
  const [apiKeyInputs, setApiKeyInputs] = useState({ google: '', openai: '', anthropic: '' });
  const formatDeployTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };
  const [showInlineAlert, setShowInlineAlert] = useState(false);
  const [inlineAlertMessage, setInlineAlertMessage] = useState('');
  const chatDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const prevGalleryModeRef = useRef(galleryViewMode);
  const chatMountRef = useRef(false);
  const chatEffectRunsRef = useRef(0);
  const infoBanner = "AI-powered lesson building: ask the Lesson Copilot to research a topic, generate an interactive lesson, and iterate on it. Switch lessons with the selector to manage multiple lessons.";

  const inferApiUrl = () => {
    const envUrl = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    if (envUrl) return envUrl;
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') {
        return 'http://localhost:8011';
      }
      return window.location.origin;
    }
    return '';
  };

  const API_URL = inferApiUrl();
  const API_ORIGIN = (() => {
    try {
      return new URL(API_URL, window.location.origin).origin;
    } catch {
      return '';
    }
  })();
  const API_ORIGIN_ALT = API_ORIGIN ? API_ORIGIN.replace('localhost', '127.0.0.1') : '';
  const IFRAME_URL = `${API_URL}/board.html`;
  const buildIframeSrc = (templateId, nonce = null) => {
    const token = nonce ?? Date.now();
    return `${IFRAME_URL}?template=${encodeURIComponent(templateId)}&_t=${token}`;
  };
  const BRAND_LOGOS = {
    arbiteria: `${API_URL}/brand_logos/Arbiteria_withTL.png`,
    zero2ai: `${API_URL}/brand_logos/zero2AI._logomain.svg`
  };
  const getIframeOrigin = () => {
    const src = iframeRef.current?.src;
    if (!src) return API_ORIGIN || API_ORIGIN_ALT || '*';
    try {
      return new URL(src, window.location.origin).origin;
    } catch {
      return API_ORIGIN || API_ORIGIN_ALT || '*';
    }
  };

  // Generate or retrieve session ID for ownership tracking
  const getSessionId = () => {
    let sessionId = localStorage.getItem('lesson_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('lesson_session_id', sessionId);
    }
    return sessionId;
  };

  const [templateOwnership, setTemplateOwnership] = useState({});

  const checkTemplateOwnership = async (templateId) => {
    if (!templateId) return false;

    const sessionId = getSessionId();
    try {
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateId)}/ownership?owner_id=${encodeURIComponent(sessionId)}`);
      if (response.ok) {
        const data = await response.json();
        setTemplateOwnership(prev => ({
          ...prev,
          [templateId]: data
        }));
        return data.is_owner;
      }
    } catch (error) {
      console.error('Error checking template ownership:', error);
    }
    return false;
  };

  useEffect(() => {
    document.title = 'LessonGen | AI Lesson Builder';
    // Intentional one-time setup for title
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, isLessonLoading]);

  // Chat floating drag handlers
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!chatDragRef.current.dragging) return;
      setChatPosition({
        x: Math.max(8, chatDragRef.current.offsetX + e.clientX),
        y: Math.max(8, chatDragRef.current.offsetY + e.clientY),
      });
    };
    const handleMouseUp = () => {
      chatDragRef.current.dragging = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Nudge chat icon when minimized
  useEffect(() => {
    chatEffectRunsRef.current += 1;
    if (!chatMountRef.current || chatEffectRunsRef.current <= 2) {
      chatMountRef.current = true;
      return;
    }
    if (chatMinimized) {
      setChatNudge(true);
      setChatTooltip(true);
      const t1 = setTimeout(() => setChatNudge(false), 1800);
      const t2 = setTimeout(() => setChatTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    // clear any lingering tooltip when opened
    setChatNudge(false);
    setChatTooltip(false);
  }, [chatMinimized]);

  // Keep ref in sync so create-success handler knows if user switched to another lesson
  useEffect(() => {
    selectedTemplateRef.current = selectedTemplate;
  }, [selectedTemplate]);

  // Nudge gallery icon when entering edit mode (gallery -> canvas)
  useEffect(() => {
    if (prevGalleryModeRef.current === true && galleryViewMode === false) {
      setChatDocked(true);
      setChatCollapsed(false);
      setChatMinimized(true);
      setGalleryNudge(true);
      setGalleryTooltip(true);
      const t1 = setTimeout(() => setGalleryNudge(false), 1800);
      const t2 = setTimeout(() => setGalleryTooltip(false), 2800);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prevGalleryModeRef.current = galleryViewMode;
  }, [galleryViewMode]);


  // Get state from iframe using postMessage (cross-origin safe)
  const getStateFromIframe = (callback, retries = 3) => {
    const iframe = iframeRef.current;
    if (!iframe) {
      console.warn('Iframe ref is not available');
      callback(null);
      return;
    }

    // Check if iframe is loaded
    try {
      if (!iframe.contentWindow) {
        console.warn('Iframe contentWindow is not available');
        if (retries > 0) {
          // Retry after a short delay
          setTimeout(() => getStateFromIframe(callback, retries - 1), 500);
          return;
        }
        callback(null);
        return;
      }
    } catch (e) {
      console.warn('Cannot access iframe contentWindow (cross-origin):', e);
      // This is expected for cross-origin, continue with postMessage
    }

    let messageReceived = false;
    const handleMessage = (event) => {
      // Accept messages from backend origin
      const isBackendOrigin = event.origin === API_ORIGIN || event.origin === API_ORIGIN_ALT;
      
      if (!isBackendOrigin) {
        return;
      }
      
      if (event.data && event.data.type === 'STATE_DATA') {
        messageReceived = true;
        window.removeEventListener('message', handleMessage);
        callback(event.data.payload);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Request state from iframe
    try {
      iframe.contentWindow.postMessage({ type: 'GET_STATE' }, getIframeOrigin());
    } catch (e) {
      console.error('Error sending postMessage to iframe:', e);
      window.removeEventListener('message', handleMessage);
      if (retries > 0) {
        // Retry after a short delay
        setTimeout(() => getStateFromIframe(callback, retries - 1), 500);
        return;
      }
      callback(null);
      return;
    }
    
    // Timeout after 3 seconds (increased from 1 second)
    setTimeout(() => {
      if (!messageReceived) {
        window.removeEventListener('message', handleMessage);
        if (retries > 0) {
          // Retry
          console.log(`Retrying getStateFromIframe, ${retries} retries left`);
          getStateFromIframe(callback, retries - 1);
        } else {
          console.warn('Timeout waiting for STATE_DATA from iframe');
          callback(null);
        }
      }
    }, 3000);
  };

  const handleSave = () => {
    const iframe = iframeRef.current;
    if (!iframe) {
      setSaveStatus('Error: Iframe not available');
      setTimeout(() => setSaveStatus(''), 2000);
      return;
    }

	    // Check if iframe is still loading
	    if (isLessonLoading) {
	      setSaveStatus('Please wait for lesson to load...');
	      setTimeout(() => setSaveStatus(''), 2000);
	      return;
	    }

    setSaveStatus('Saving...');
    getStateFromIframe((data) => {
      if (data) {
        const payload = { ...data, savedAt: new Date().toISOString() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        setSaveStatus('Error: Could not access iframe. Please try again.');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    });
  };

  const handleClear = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    // Clear state in iframe via postMessage
    const iframe = iframeRef.current;
    if (iframe) {
      const emptyData = { inputs: [], textareas: [] };
      iframe.contentWindow.postMessage({
        type: 'RESTORE_STATE',
        payload: emptyData
      }, getIframeOrigin());
    }
    setSaveStatus('Cleared');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

	  const handleDeploy = async () => {
    if (!selectedTemplate) return;
    setIsDeploying(true);
    setDeploymentStatus(null);
    setDeploymentUrl(null);

	    try {
	      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deploy`, {
	        method: 'POST',
	        headers: { 'Content-Type': 'application/json' },
	      });

      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        const raw = await response.text();
        throw new Error(`Deploy API returned non-JSON response (${response.status}). ${raw?.slice(0, 300) || ''}`);
      }

      // Backward compatibility: older backend returned [payload, status_code]
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        data = data[0];
      }
      if (!response.ok) {
        setDeploymentStatus('error');
        setDeploymentUrl(null);
        const errorMsg = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
        alert(`Deployment failed: ${errorMsg}`);
        return;
      }

      if (data.success) {
        setDeploymentStatus('success');
        const url = data.deployment_url || data.production_url;
        setDeploymentUrl(url);
        if (data.deployment) {
          setExistingDeployment(data.deployment);
        } else if (url) {
          setExistingDeployment({
            deployment_url: url,
            deployed_at: new Date().toISOString()
          });
        }
      } else {
        setDeploymentStatus('error');
        setDeploymentUrl(null);
        const errorMsg = data.error || data.message || 'Unknown error';
        alert(`Deployment failed: ${errorMsg}`);
      }
    } catch (error) {
      setDeploymentStatus('error');
      setDeploymentUrl(null);
      alert(`Error deploying lesson: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleDeployClick = () => setShowDeployConfirmModal(true);

  const handleDeployConfirm = () => {
    setShowDeployConfirmModal(false);
    setShowDeployModal(true);
    handleDeploy();
  };

  const openInlineAlert = (message) => {
    setInlineAlertMessage(message);
    setShowInlineAlert(true);
  };

  // Load available templates on mount
  useEffect(() => {
	    const loadTemplates = async () => {
	      try {
	        console.log('Loading templates from:', `${API_URL}/board/templates`);
	        const response = await fetch(`${API_URL}/board/templates`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Templates response:', data);
        setAvailableTemplates(data.templates || []);
        if (data.templates && data.templates.length > 0) {
          setSelectedTemplate(prevTemplate => {
            const currentExists = data.templates.find(t => t.id === prevTemplate);
            return currentExists ? prevTemplate : data.templates[0].id;
          });
        } else {
          setSelectedTemplate(null);
        }
      } catch (error) {
        console.error('Error loading templates:', error);
        // Keep default template if fetch fails
      }
    };
    loadTemplates();
  }, [API_URL, selectedTemplate, iframeNonce, galleryViewMode]);

  const BYOK_STORAGE_KEYS = { google: 'appstorm_byok_google', openai: 'appstorm_byok_openai', anthropic: 'appstorm_byok_anthropic' };
  const getStoredApiKeys = () => ({
    google: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.google)) || '',
    openai: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.openai)) || '',
    anthropic: (typeof localStorage !== 'undefined' && localStorage.getItem(BYOK_STORAGE_KEYS.anthropic)) || '',
  });
  const getProvidersSetFromStorage = () => {
    const k = getStoredApiKeys();
    return {
      google: !!(k.google && k.google.trim()),
      openai: !!(k.openai && k.openai.trim()),
      anthropic: !!(k.anthropic && k.anthropic.trim()),
    };
  };

  const fetchKeysConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/board/keys`);
      if (res.ok) {
        const data = await res.json();
        const providers_set = getProvidersSetFromStorage();
        setApiKeysConfig({
          providers_set,
          enabled_models: [],
          use_user_keys_only: !!data.use_user_keys_only,
        });
      }
    } catch (e) {
      console.error('Error fetching keys config:', e);
      setApiKeysConfig(prev => ({ ...prev, providers_set: getProvidersSetFromStorage() }));
    }
  };

  useEffect(() => {
    if (!API_URL) return;
    setApiKeyInputs(getStoredApiKeys());
    fetchKeysConfig();
  }, [API_URL]);

  const normalizeModelId = (candidate) => {
    if (!candidate) return '';
    const clean = candidate.trim().toLowerCase();
    if (LEGACY_OPENAI_MODELS.includes(clean)) return OPENAI_MODEL_ID;
    return clean;
  };
  const modelToProvider = {
    gemini: 'google',
    [OPENAI_MODEL_ID]: 'openai',
    gpt5: 'openai',
    'claude-sonnet-4-5': 'anthropic',
    'claude-opus-4-6': 'anthropic',
  };
  const isModelEnabled = (modelId) => {
    const normalized = normalizeModelId(modelId) || modelId;
    if (!apiKeysConfig.use_user_keys_only) return true;
    const provider = modelToProvider[normalized] || modelToProvider[modelId];
    return provider && apiKeysConfig.providers_set?.[provider];
  };
  const enabledModelIds = ['gemini', OPENAI_MODEL_ID, 'claude-sonnet-4-5', 'claude-opus-4-6'].filter(isModelEnabled);
  useEffect(() => {
    if (apiKeysConfig.use_user_keys_only && enabledModelIds.length && !enabledModelIds.includes(newTemplateModel)) {
      setNewTemplateModel(enabledModelIds[0]);
    }
  }, [apiKeysConfig.use_user_keys_only, enabledModelIds.join(','), newTemplateModel]);

  const saveApiKeys = () => {
    if (typeof localStorage !== 'undefined') {
      if (apiKeyInputs.google != null) localStorage.setItem(BYOK_STORAGE_KEYS.google, apiKeyInputs.google || '');
      if (apiKeyInputs.openai != null) localStorage.setItem(BYOK_STORAGE_KEYS.openai, apiKeyInputs.openai || '');
      if (apiKeyInputs.anthropic != null) localStorage.setItem(BYOK_STORAGE_KEYS.anthropic, apiKeyInputs.anthropic || '');
    }
    setApiKeysConfig(prev => ({ ...prev, providers_set: getProvidersSetFromStorage() }));
    setShowKeysModal(false);
  };

  // Check ownership when template changes
  useEffect(() => {
    if (selectedTemplate) {
      if (pendingTemplateId && selectedTemplate === pendingTemplateId) {
        return;
      }
      checkTemplateOwnership(selectedTemplate);
    }
  }, [selectedTemplate, pendingTemplateId]);

  // Fetch existing deployment info when template changes
  useEffect(() => {
	    const fetchDeploymentInfo = async () => {
      if (!selectedTemplate) return;
      if (pendingTemplateId && selectedTemplate === pendingTemplateId) {
        return;
      }
	      try {
	        const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/deployment`);
        const data = await response.json();
        if (data.success && data.deployment) {
          setExistingDeployment(data.deployment);
        } else {
          setExistingDeployment(null);
        }
      } catch (error) {
        console.error('Error fetching deployment info:', error);
        setExistingDeployment(null);
      } finally {
        setDeploymentStatus(null);
        setDeploymentUrl(null);
        setShowDeployModal(false);
        setShowDeployConfirmModal(false);
      }
    };
    fetchDeploymentInfo();
  }, [selectedTemplate, API_URL, pendingTemplateId]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  // Handle iframe load events to show/hide loading indicator and catch errors
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      // If iframe ref is not ready, try again after a short delay
      const retryTimeout = setTimeout(() => {
        if (iframeRef.current) {
          setIsLessonLoading(false);
        }
      }, 1000);
      return () => clearTimeout(retryTimeout);
    }

    const handleLoad = () => {
      console.log('Iframe load event fired, hiding loading indicator');
      // Clear timeout if iframe loads successfully
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      // Hide loading immediately when load event fires
      // postMessage is just a bonus confirmation, but load event is reliable
      setIsLessonLoading(false);
      setLessonError(null); // Clear any previous errors on successful load
      // Inject lightweight nav guard to keep pagination responsive
      try {
        const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
        if (doc) {
          injectNavGuard(doc);
        }
      } catch (err) {
        console.warn('Nav guard injection skipped:', err);
      }
    };

	    const handleError = (event) => {
	      console.error('Iframe load error:', event);
	      setIsLessonLoading(false);
	      setLessonError('Failed to load lesson. Please check if the backend server is running.');
	    };

    // Set loading initially
    setIsLessonLoading(true);
    setLessonError(null);

    // Listen for load and error events
    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    // Also check if iframe is already loaded (with retry for cross-origin)
    const checkIfLoaded = () => {
      try {
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
          console.log('Iframe already loaded (same-origin)');
          handleLoad();
          return true;
        }
      } catch (e) {
        // Cross-origin, can't access directly - that's expected
        console.log('Cannot access iframe document (cross-origin - expected)');
        // For cross-origin, we rely on postMessage or load event
        // Check if iframe src has loaded by checking if it's not blank
        if (iframe.src && iframe.src !== 'about:blank') {
          // Give it a moment, then check if load event fires
          setTimeout(() => {
            // If still loading after 2 seconds, try to trigger load check
            if (isLessonLoading) {
              console.log('Iframe src set but no load event yet, waiting for postMessage...');
            }
          }, 2000);
        }
      }
      return false;
    };
    
    // Check immediately
    if (!checkIfLoaded()) {
      // Also check after a short delay
      setTimeout(checkIfLoaded, 500);
    }

    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Also check for errors after a timeout
    loadingTimeoutRef.current = setTimeout(() => {
      // If still loading after 15 seconds, show error
      console.log('Loading timeout reached, checking iframe state');
      // Check if iframe actually loaded but message wasn't received
      if (iframeRef.current) {
        try {
          // Try to access iframe to see if it loaded
          const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
          if (iframeDoc && iframeDoc.readyState === 'complete') {
            console.log('Iframe loaded but message not received, hiding loading');
            setIsLessonLoading(false);
            return;
          }
        } catch (e) {
          // Cross-origin, can't access - that's expected
          console.log('Cannot access iframe (cross-origin), checking network');
        }
      }
      
      // Check if backend is reachable
      fetch(`${API_URL}/board/last-modified`)
        .then(() => {
          // Backend is reachable, iframe might just be slow
          setLessonError('Artifacts are taking longer than expected to load. The backend server is running. Please try refreshing or check the browser console for errors.');
        })
        .catch(() => {
          // Backend is not reachable
          setLessonError('Cannot connect to backend server. Please ensure the backend server is running and REACT_APP_API_URL is set.');
        });
      
      setIsLessonLoading(false);
    }, 15000); // Increased to 15 seconds

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [API_URL]);

  // Listen for IFRAME_LOADED message from LessonIframe to hide loading
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('Received postMessage:', event.data, 'from origin:', event.origin);
      
      // Accept messages from backend origin (iframe) or same origin
      const isBackendOrigin = event.origin === API_ORIGIN || 
                              event.origin === API_ORIGIN_ALT ||
                              event.origin === window.location.origin ||
                              event.origin === '*';
      
      if (!isBackendOrigin && event.origin !== '*') {
        console.log('Ignoring message from origin:', event.origin);
        return;
      }
      
      if (event.data && event.data.type === 'IFRAME_LOADED') {
        console.log('IFRAME_LOADED message received, hiding loading indicator');
        // Clear timeout if message received
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        setLessonError(null); // Clear errors on successful load
      } else if (event.data && event.data.type === 'IFRAME_ERROR') {
        console.log('IFRAME_ERROR message received:', event.data.error);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        setLessonError(event.data.error || 'Failed to load artifacts content.');
      } else if (event.data && event.data.type === 'STATE_RESTORED') {
        // Also hide loading when state is restored (means iframe loaded)
        console.log('STATE_RESTORED message received, iframe is loaded');
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
        setIsLessonLoading(false);
        setLessonError(null);
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('Added message listener for IFRAME_LOADED');
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleTemplateChange = (templateId) => {
    if (templateId === 'add-new') {
      setShowAddTemplateModal(true);
      return;
    }
    setSelectedTemplate(templateId);
    const nonce = Date.now();
    setIframeNonce(nonce);
    // Reload iframe with new template
    if (iframeRef.current) {
      setIsLessonLoading(true);
      const newSrc = buildIframeSrc(templateId, nonce);
      iframeRef.current.src = newSrc;
    }
  };

	  const handleDeleteClick = (templateId) => {

    const template = availableTemplates.find(t => t.id === templateId);
    setTemplateToDelete({ id: templateId, name: template ? template.name : templateId });
    setDeleteStep(1);
    setShowDeleteModal(true);
  };

  const handleCopyClick = (templateId) => {
    const template = availableTemplates.find(t => t.id === templateId);
    const baseName = template ? template.name : templateId;
    setCopyLessonName(`${baseName} Copy`);
    setShowCopyModal(true);
  };

	  const handleCopyLesson = async () => {
	    if (!copyLessonName.trim()) {
	      alert('Please enter a name for the copied lesson');
	      return;
	    }

    setIsCopying(true);
	    try {
	      const sessionId = getSessionId();
	      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(selectedTemplate)}/copy`, {
	        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_name: copyLessonName.trim(),
          owner_id: sessionId
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Copy failed');
      }

	      const templatesResponse = await fetch(`${API_URL}/board/templates`);
      const templatesData = await templatesResponse.json();
      if (templatesData.templates && templatesData.templates.length > 0) {
        setAvailableTemplates(templatesData.templates);
      }

      setSelectedTemplate(data.template_id);
      setShowCopyModal(false);
      setCopyLessonName('');
      clearTemplateChatHistory(data.template_id);
      checkTemplateOwnership(data.template_id);

      if (iframeRef.current) {
        setIsLessonLoading(true);
        const nonce = Date.now();
        setIframeNonce(nonce);
        iframeRef.current.src = buildIframeSrc(data.template_id, nonce);
      }
	    } catch (error) {
	      console.error('Error copying lesson:', error);
	      alert('Failed to copy lesson. Please try again.');
	    } finally {
	      setIsCopying(false);
	    }
	  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    if (deleteStep === 1) {
      // First confirmation - move to second step
      setDeleteStep(2);
      return;
    }

    // Second confirmation - proceed with deletion
    setIsDeletingTemplate(true);
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API_URL}/board/templates/${encodeURIComponent(templateToDelete.id)}?owner_id=${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        // Clear chat history for the deleted template BEFORE reloading templates
        console.log(`[App] Deleting lesson ${templateToDelete.id}, clearing chat history...`);
        clearTemplateChatHistory(templateToDelete.id);

        // Reload templates list
        const templatesResponse = await fetch(`${API_URL}/board/templates`);
        const templatesData = await templatesResponse.json();
        if (templatesData.templates && templatesData.templates.length > 0) {
          setAvailableTemplates(templatesData.templates);
          if (selectedTemplate === templateToDelete.id) {
            const next = templatesData.templates[0].id;
            setSelectedTemplate(next);
            if (iframeRef.current) {
              setIsLessonLoading(true);
              const nonce = Date.now();
              setIframeNonce(nonce);
              iframeRef.current.src = buildIframeSrc(next, nonce);
            }
          }
        } else {
          setAvailableTemplates([]);
          if (selectedTemplate === templateToDelete.id) {
            setSelectedTemplate(null);
          }
        }
        // Close modal, reset, and go to gallery
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        setDeleteStep(1);
        setGalleryViewMode(true);
      } else {
        alert(`Error deleting lesson: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Error deleting lesson: ${error.message}`);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
    setDeleteStep(1);
  };

  const handleOpenTemplateFromGallery = (templateId) => {
    setGalleryViewMode(false); // enter slide edit view
    setLessonError(null);
    setIsLessonLoading(true); // always set so when iframe loads we clear it (async: can open other apps while one is creating)
    setSelectedTemplate(templateId);
    if (pendingTemplateId && templateId === pendingTemplateId) {
      return;
    }
    if (iframeRef.current) {
      const nonce = Date.now();
      setIframeNonce(nonce);
      iframeRef.current.src = buildIframeSrc(templateId, nonce);
    }
  };

  const MAX_TEMPLATE_IMAGES = 6;
  const readTemplateFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  const handleTemplateImageSelect = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    const valid = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Please select only image files');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`Image ${file.name} must be less than 10MB`);
        continue;
      }
      valid.push(file);
    }
    const toAdd = valid.slice(0, MAX_TEMPLATE_IMAGES - newTemplateImages.length);
    if (toAdd.length === 0) {
      alert(`You can add up to ${MAX_TEMPLATE_IMAGES} reference images.`);
      if (templateImageInputRef.current) templateImageInputRef.current.value = '';
      return;
    }
    const newFiles = [...newTemplateImages, ...toAdd].slice(0, MAX_TEMPLATE_IMAGES);
    setNewTemplateImages(newFiles);
    try {
      const newPreviews = await Promise.all(toAdd.map(readTemplateFileAsDataURL));
      setNewTemplateImagePreviews((prev) => [...prev, ...newPreviews].slice(0, MAX_TEMPLATE_IMAGES));
    } catch (err) {
      console.error('Error reading template images:', err);
      setNewTemplateImages(newTemplateImages);
    }
    if (templateImageInputRef.current) templateImageInputRef.current.value = '';
  };

  const removeTemplateImage = (index) => {
    if (index !== undefined) {
      setNewTemplateImages((prev) => prev.filter((_, i) => i !== index));
      setNewTemplateImagePreviews((prev) => prev.filter((_, i) => i !== index));
    } else {
      setNewTemplateImages([]);
      setNewTemplateImagePreviews([]);
    }
    if (templateImageInputRef.current) templateImageInputRef.current.value = '';
  };

  const handleTemplateFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!['csv', 'xlsx', 'xls', 'pdf', 'docx', 'pptx'].includes(fileExt)) {
        alert('Please select a supported file (.csv, .xlsx, .xls, .pdf, .docx, .pptx)');
        return;
      }
      
      // Validate file size (max 50MB for data files)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }
      
      setNewTemplateFile(file);
      
      // Upload the file immediately
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData,
        });
        
        const data = await response.json();
        if (data.success) {
          setNewTemplateFilePath(data.file_path);
          console.log(`Template file uploaded: ${data.file_path}`);
        } else {
          alert(`Upload failed: ${data.error || 'Unknown error'}`);
          setNewTemplateFile(null);
        }
      } catch (error) {
        console.error('Error uploading template file:', error);
        alert('Failed to upload file. Please try again.');
        setNewTemplateFile(null);
      }
    }
  };

  const removeTemplateFile = () => {
    setNewTemplateFile(null);
    setNewTemplateFilePath(null);
    if (templateFileInputRef.current) {
      templateFileInputRef.current.value = '';
    }
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data URL prefix (data:image/png;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const deriveLessonName = (description) => {
    const cleaned = (description || '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return 'New Lesson';
    const firstSentence = cleaned.split(/[.!?]/)[0] || cleaned;
    const words = firstSentence.split(' ').filter(Boolean).slice(0, 6);
    return words.join(' ') || 'New Lesson';
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateDescription.trim()) {
      alert('Please provide a lesson description');
      return;
    }
    if (createInFlightRef.current) {
      return; // already creating (e.g. double-click) -- only one create at a time
    }
    createInFlightRef.current = true;
    setIsCreatingTemplate(true);
    
    // Save form data before clearing
    const previousTemplateId = selectedTemplate;
    const savedName = newTemplateName.trim() || deriveLessonName(newTemplateDescription);
    const savedDescription = newTemplateDescription.trim();
    const imagesToSend = [...newTemplateImages];
    setCreatingTemplateName(savedName);
    setCreatingTemplateDescription(savedDescription);

    // Optimistically add the template to the list (use unique id so only this card shows "Creating...")
    const tempTemplateId = `creating-${Date.now()}`;
    const tempTemplate = { id: tempTemplateId, name: savedName };
    setAvailableTemplates(prev => [...prev, tempTemplate]);
    setSelectedTemplate(tempTemplateId);
    setPendingTemplateId(tempTemplateId);
    setIsLessonLoading(true);
    setLessonError(null);

    // Close modal immediately for better UX
    setShowAddTemplateModal(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateModel(OPENAI_MODEL_ID); // Reset to default model
    
    // Prepare image data (multiple)
    const imagesPayload = [];
    for (let i = 0; i < imagesToSend.length; i++) {
      const b64 = await convertImageToBase64(imagesToSend[i]);
      imagesPayload.push({ base64: b64, mime_type: imagesToSend[i].type || 'image/png' });
    }
    
    // Clear image and file state
    removeTemplateImage();
    removeTemplateFile();
    
    try {
      const requestBody = {
        name: savedName,
        description: savedDescription,
        model: newTemplateModel,
        owner_id: getSessionId()
      };
      const storedKeys = getStoredApiKeys();
      if (storedKeys.google?.trim()) requestBody.google_key = storedKeys.google.trim();
      if (storedKeys.openai?.trim()) requestBody.openai_key = storedKeys.openai.trim();
      if (storedKeys.anthropic?.trim()) requestBody.anthropic_key = storedKeys.anthropic.trim();

      if (imagesPayload.length === 1) {
        requestBody.image_base64 = imagesPayload[0].base64;
        requestBody.image_mime_type = imagesPayload[0].mime_type;
      } else if (imagesPayload.length > 1) {
        requestBody.images = imagesPayload;
      }
      
      if (newTemplateFilePath) {
        requestBody.uploaded_file_path = newTemplateFilePath;
      }
      
      const response = await fetch(`${API_URL}/board/templates/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const rawText = await response.text();
      let data = {};
      const parseJsonSafely = (text) => {
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch (error) {
          const match = text.match(/\{[\s\S]*\}/);
          if (match) {
            try {
              return JSON.parse(match[0]);
            } catch (innerError) {
              return {};
            }
          }
          return {};
        }
      };
      data = parseJsonSafely(rawText);
      const messageFromData = data.block_reason || data.error || data.reason || data.message || '';
      const messageFromText = rawText && !/^\s*[\{\[]/.test(rawText) ? rawText : '';
      const resolvedMessage = messageFromData || messageFromText || 'Request failed. Please try again.';
      
      if (!response.ok) {
        const msg = messageFromData || messageFromText || `HTTP ${response.status}: ${response.statusText}`;
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        setPendingTemplateId(null);
          setSelectedTemplate(previousTemplateId);
          if (iframeRef.current) {
            setIsLessonLoading(true);
            const nonce = Date.now();
            setIframeNonce(nonce);
            iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
          }
        setIsLessonLoading(false);
        setCreatingTemplateName('');
        setCreatingTemplateDescription('');
        openInlineAlert(msg);
        setIsCreatingTemplate(false);
        return;
      }
      
      if (data.blocked) {
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        setPendingTemplateId(null);
        setSelectedTemplate(previousTemplateId);
        if (iframeRef.current) {
          setIsLessonLoading(true);
          const nonce = Date.now();
          setIframeNonce(nonce);
          iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
        }
        setIsLessonLoading(false);
        setCreatingTemplateName('');
        setCreatingTemplateDescription('');
        openInlineAlert(resolvedMessage);
        setIsCreatingTemplate(false);
        return;
      }

      if (data.success) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        // Reload templates list to get the actual template data
        const templatesResponse = await fetch(`${API_URL}/board/templates`);
        const templatesData = await templatesResponse.json();
        if (templatesData.templates && templatesData.templates.length > 0) {
          setAvailableTemplates(templatesData.templates);
          localStorage.setItem(`chat_model_${data.template_id}`, newTemplateModel);
          setPendingTemplateId(null);
          // Only switch to the new template if user is still viewing the creating placeholder (async: they can open other apps while creating)
          if (selectedTemplateRef.current === tempTemplateId) {
            setSelectedTemplate(data.template_id);
            if (iframeRef.current) {
              setIsLessonLoading(true);
              setLessonError(null);
              const nonce = Date.now();
              setIframeNonce(nonce);
              iframeRef.current.src = buildIframeSrc(data.template_id, nonce);
            }
          } else {
            openInlineAlert(`"${savedName}" is ready. Open it from the gallery.`);
          }
        }
        if (data.has_validation_warnings) {
          const warningLines = Array.isArray(data.validation_errors) ? data.validation_errors : [];
          const warningPreview = warningLines.length
            ? warningLines.slice(0, 3).join(' | ')
            : 'Automatic integrity checks reported warnings.';
          openInlineAlert(`Lesson created with warnings: ${warningPreview}`);
        }
        setPendingTemplateId(null);
      } else {
        setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
        setPendingTemplateId(null);
        setSelectedTemplate(previousTemplateId);
        if (iframeRef.current) {
          setIsLessonLoading(true);
          const nonce = Date.now();
          setIframeNonce(nonce);
          iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
        }
        setIsLessonLoading(false);
        setCreatingTemplateName('');
        setCreatingTemplateDescription('');
        openInlineAlert(`Error creating lesson: ${resolvedMessage}`);
      }
    } catch (error) {
      console.error('Error creating template:', error);
      setAvailableTemplates(prev => prev.filter(t => t.id !== tempTemplateId));
      setPendingTemplateId(null);
      setSelectedTemplate(previousTemplateId);
      if (iframeRef.current) {
        setIsLessonLoading(true);
        const nonce = Date.now();
        setIframeNonce(nonce);
        iframeRef.current.src = buildIframeSrc(previousTemplateId, nonce);
      }
      setIsLessonLoading(false);
      setCreatingTemplateName('');
      setCreatingTemplateDescription('');
      openInlineAlert(`Error creating lesson: ${error.message}`);
    } finally {
      createInFlightRef.current = false;
      setIsCreatingTemplate(false);
      setCreatingTemplateName('');
      setCreatingTemplateDescription('');
    }
  };

  return (
    <div className="App">
	      <div className={`layout-shell ${chatDocked && !galleryViewMode ? 'chat-docked' : ''} ${chatDocked && !galleryViewMode && chatCollapsed ? 'chat-collapsed' : ''}`}>
      <div className={`left-rail ${galleryViewMode ? 'gallery-mode' : ''}`}>
	        <aside className="side-rail">
          <div className="brand-mark mini">
            <img
              src="/logo/zero2AI_logomain.svg"
              alt="Zero2AI Logo"
              className="brand-logo mini"
            />
          </div>
          <div className="rail-actions">
            <button className="rail-btn icon rail-new" onClick={() => setShowAddTemplateModal(true)} title="New Lesson">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 5v14M5 12h14"></path>
              </svg>
              <span className="sr-only">New Lesson</span>
            </button>
            <button 
              className={`rail-btn icon rail-gallery ${galleryNudge ? 'nudge' : ''}`} 
              onClick={() => setGalleryViewMode(true)} 
              title="Gallery"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                <rect x="3" y="14" width="7" height="7" rx="1"></rect>
                <rect x="14" y="14" width="7" height="7" rx="1"></rect>
              </svg>
              <span className="sr-only">Gallery</span>
              {galleryTooltip && <span className="rail-tooltip">Go to Gallery</span>}
            </button>
            <button 
              className={`rail-btn icon rail-chat ${chatNudge ? 'nudge' : ''}`} 
	              onClick={() => {
	                setChatDocked(true);
	                setChatCollapsed(false);
	                setChatMinimized(true);
	              }} 
	              title="Lesson Copilot"
	            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 11.5a8.38 8.38 0 0 1-1.9 5.4 8.5 8.5 0 0 1-6.6 3.1 8.38 8.38 0 0 1-5.4-1.9L3 20l1.9-4.1a8.38 8.38 0 0 1-1.9-5.4 8.5 8.5 0 0 1 3.1-6.6A8.38 8.38 0 0 1 11.5 2H12a8.5 8.5 0 0 1 8 9.5Z"></path>
              </svg>
              <span className="sr-only">Open Lesson Copilot</span>
              {chatTooltip && <span className="rail-tooltip">Lesson Copilot</span>}
            </button>
            <button
              className="rail-btn icon rail-keys"
              onClick={() => setShowKeysModal(true)}
              title="API Keys (Google, OpenAI, Anthropic)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="7" cy="12" r="3"></circle>
                <path d="M10 12h11"></path>
                <path d="M18 12v3"></path>
                <path d="M15 12v2"></path>
              </svg>
              <span className="sr-only">API Keys</span>
              <span className="rail-tooltip auto-hide">API Keys</span>
            </button>
          </div>
        </aside>
	        {chatDocked && !galleryViewMode && (
	          <aside className={`chat-sidebar ${chatCollapsed ? 'collapsed' : ''}`}>
	            <div className="chat-sidebar-header">
	              {!chatCollapsed && <span className="chat-sidebar-title">Lesson Copilot</span>}
	              <div className="chat-sidebar-actions">
                <button
                  className="icon-btn small"
                  onClick={() => setChatCollapsed(!chatCollapsed)}
                  title={chatCollapsed ? 'Expand chat' : 'Collapse chat'}
                >
                  {chatCollapsed ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  )}
                  <span className="sr-only">{chatCollapsed ? 'Expand chat' : 'Collapse chat'}</span>
                </button>
                <button
                  className="icon-btn small"
                  onClick={() => {
                    setChatDocked(false);
                    setChatMinimized(false);
                  }}
                  title="Pop out chat"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 3h3v3"></path>
                    <path d="M21 3l-7 7"></path>
                    <rect x="3" y="7" width="14" height="14" rx="2" ry="2"></rect>
                  </svg>
                  <span className="sr-only">Pop out chat</span>
                </button>
              </div>
            </div>
            {!chatCollapsed && (
              <div className="chat-sidebar-body">
                {selectedTemplate ? (
                  <ChatAdvisor
                    key={`chat-${selectedTemplate}`}
                    selectedTemplate={selectedTemplate}
                    isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
                    isCreatingTemplate={isCreatingTemplate}
                    creatingTemplateDescription={creatingTemplateDescription}
                    pendingTemplateId={pendingTemplateId}
                    apiKeysConfig={apiKeysConfig}
                    defaultModelForTemplate={availableTemplates.find(t => t.id === selectedTemplate)?.default_model}
                    ownerId={getSessionId()}
                  />
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>It's empty</p>
                    <p>Select or create a lesson to chat with the copilot.</p>
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
	          <div className="main-pane">
          <div className="top-actions compact">
            <img
              src="/logo/Arbiteria_withTL.png"
              alt="Arbiteria logo"
              className="top-right-logo-abs"
            />
            <div className="top-brand-center">
              <div className="brand-mark mini center">
                <div className="brand-circles mini">
                  <span></span><span></span><span></span>
                </div>
                <div className="brand-type">
                  <div className="brand-kicker">
                    <span className="brand-lesson">Lesson</span>
                    <span className="brand-gen">Gen</span>
                  </div>
                  <div className="brand-tagline">Interactive mini-lessons</div>
                </div>
              </div>
            </div>
            <div className="top-actions-mid">
              {!galleryViewMode && selectedTemplate && (
                <span className={`owner-pill ${templateOwnership[selectedTemplate]?.is_owner ? 'owner' : 'readonly'}`}>
                  {templateOwnership[selectedTemplate]?.is_owner ? 'Owner' : 'Read-only'}
                </span>
              )}
            </div>
            <div className="top-actions-right">
              <span className="save-status">{saveStatus}</span>
              <button className="icon-btn" onClick={handleSave} title="Save inputs">
                <span aria-hidden>💾</span>
                <span className="sr-only">Save</span>
              </button>
              <button className="icon-btn" onClick={handleClear} title="Clear saved state">
                <span aria-hidden>🧹</span>
                <span className="sr-only">Clear</span>
              </button>
            </div>
          </div>
      <div className={`main-layout ${isFullscreen ? 'fullscreen-mode' : ''} ${galleryViewMode ? 'gallery-view-mode' : ''}`}>
        <div className={`lesson-column canvas ${isFullscreen ? 'fullscreen' : ''} ${galleryViewMode ? 'gallery-view' : ''}`}>
          {galleryViewMode ? (
            <div className="inline-gallery">
              {availableTemplates.length === 0 ? (
                <div className="gallery-empty-state" style={{ textAlign: 'center', padding: '3rem', color: '#facc15' }}>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>It's empty here</p>
                  <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No lessons yet.</p>
                  <p style={{ marginBottom: '1rem' }}>Create your first lesson to get started.</p>
                  <button
                    className="modal-button primary"
                    onClick={() => setShowAddTemplateModal(true)}
                  >
                    Create lesson
                  </button>
                </div>
              ) : (
              <div className="gallery-grid inline">
                {availableTemplates.map(template => (
                  <div 
                    key={template.id} 
                    className="gallery-item inline"
                    onClick={() => handleOpenTemplateFromGallery(template.id)}
                    title={`Open ${template.name}`}
                  >
                    <div className="gallery-card">
                      <div className="gallery-face front">
                        <div className="gallery-hover-anchor">
                          <div className="gallery-hover-bubble">
                            <p className="bubble-title">{template.name}</p>
                            <p className="bubble-text">
                              {template.description && template.description.trim().length > 0
                                ? template.description
                                : 'Interactive lesson with multimedia screens, checks, and a scored assessment.'}
                            </p>
                            <p className="bubble-text subtle">
                              {template.estimated_minutes
                                ? `${template.estimated_minutes} min · ARCS hooks + assessment`
                                : 'ARCS hooks + assessment (80% to certify)'}
                            </p>
                            <span className="bubble-cta">Click to open & edit</span>
                          </div>
                        </div>
                        <div className="gallery-item-preview">
                          {pendingTemplateId && template.id === pendingTemplateId ? (
                            <div className="gallery-preview-pending">
                              <div className="gallery-loading-spinner"></div>
                              <span>Creating...</span>
                            </div>
                          ) : (
                            <>
                              <iframe
                                src={`${IFRAME_URL}?template=${template.id}`}
                                className="gallery-preview-iframe"
                                title={`Preview of ${template.name}`}
                                loading="lazy"
                                onLoad={(e) => {
                                  e.target.classList.add('loaded');
                                  const loadingDiv = e.target.nextElementSibling;
                                  if (loadingDiv) {
                                    loadingDiv.style.display = 'none';
                                  }
                                }}
                              />
                              <div className="gallery-preview-loading">
                                <div className="gallery-loading-spinner"></div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="gallery-item-info">
                          <h3 className="gallery-item-name">{template.name}</h3>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          ) : (
          <div className="lesson-frame" ref={lessonFrameRef}>
            {!selectedTemplate ? (
              <div className="gallery-empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '320px', textAlign: 'center', padding: '2rem', color: '#facc15' }}>
                <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>It's empty here</p>
                <p style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>No lesson selected.</p>
                <p style={{ marginBottom: '1rem' }}>Switch to gallery view to open a lesson, or create a new one.</p>
                <button
                  className="modal-button primary"
                  onClick={() => { setGalleryViewMode(true); setShowAddTemplateModal(true); }}
                >
                  Create lesson
                </button>
              </div>
            ) : (
              <>
            {/* Only show full-screen overlay when viewing the lesson that's being created; when user switched to another lesson, let that iframe load without blocking */}
            {isLessonLoading && !lessonError && (!isCreatingTemplate || selectedTemplate === pendingTemplateId) && (
              <div className="lesson-loading-overlay">
                <div className="lesson-loading-spinner">
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                  <div className="spinner-ring"></div>
                </div>
	                <div className="lesson-loading-text">
	                  {isCreatingTemplate && creatingTemplateName && selectedTemplate === pendingTemplateId
	                    ? `Creating "${creatingTemplateName}"...` 
	                    : 'Loading lesson...'}
	                </div>
	              </div>
	            )}
	            {lessonError && (
	              <div className="lesson-error-overlay">
	                <div className="lesson-error-icon">!</div>
	                <div className="lesson-error-title">Error Loading Lesson</div>
	                <div className="lesson-error-message">{lessonError}</div>
	              <button
                  className="lesson-error-retry"
                  onClick={() => {
                    setLessonError(null);
                    setIsLessonLoading(true);
                    if (iframeRef.current) {
                      const currentSrc = iframeRef.current.src;
                      iframeRef.current.src = '';
                      setTimeout(() => {
                        if (iframeRef.current) {
                          iframeRef.current.src = currentSrc;
                        }
                      }, 100);
                    }
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            <div className="floating-lesson-actions">
              {!galleryViewMode && (
                <>
                  <button
                    className="icon-btn round action-deploy"
                    onClick={handleDeployClick}
                    disabled={isDeploying}
                    title={existingDeployment ? "Redeploy to Cloudflare Pages (will create new link if file changed)" : "Deploy to Cloudflare Pages"}
                  >
                    {isDeploying ? (
                      <div className="deploy-spinner"></div>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                    )}
                    <span className="sr-only">{existingDeployment ? 'Redeploy' : 'Deploy'}</span>
                  </button>
                  <button className="icon-btn round action-save" onClick={handleSave} title="Save inputs">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    <span className="sr-only">Save</span>
                  </button>
                  <button className="icon-btn round action-clear" onClick={handleClear} title="Clear saved state">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="1 4 1 10 7 10"></polyline>
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                    </svg>
                    <span className="sr-only">Clear</span>
                  </button>
                </>
              )}
              <button 
                className="icon-btn round action-fullscreen"
                onClick={handleFullscreen}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 3 3 3 3 9"></polyline>
                    <line x1="3" y1="3" x2="9" y2="9"></line>
                    <polyline points="15 21 21 21 21 15"></polyline>
                    <line x1="21" y1="21" x2="15" y2="15"></line>
                    <polyline points="21 9 21 3 15 3"></polyline>
                    <line x1="21" y1="3" x2="15" y2="9"></line>
                    <polyline points="3 15 3 21 9 21"></polyline>
                    <line x1="3" y1="21" x2="9" y2="15"></line>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 3 3 3 3 9"></polyline>
                    <line x1="3" y1="3" x2="9" y2="9"></line>
                    <polyline points="15 21 21 21 21 15"></polyline>
                    <line x1="21" y1="21" x2="15" y2="15"></line>
                  </svg>
                )}
                <span className="sr-only">{isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}</span>
              </button>
              <button
                className="icon-btn round action-code"
                onClick={() => setShowCodePreview(true)}
                title="View code"
                disabled={!selectedTemplate}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
                <span className="sr-only">View code</span>
              </button>
              <button
                className="icon-btn round action-copy"
                onClick={() => handleCopyClick(selectedTemplate)}
                title={`Copy ${availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}`}
                disabled={!selectedTemplate}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
	                <span className="sr-only">Copy lesson</span>
	              </button>
              <button
                className="icon-btn round danger action-delete"
                onClick={() => handleDeleteClick(selectedTemplate)}
                title={`Delete ${availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}`}
                disabled={!selectedTemplate || !(templateOwnership[selectedTemplate]?.is_owner ?? true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                  <path d="M10 11v6"></path>
                  <path d="M14 11v6"></path>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                </svg>
                <span className="sr-only">Delete lesson</span>
              </button>
              <button
                className="icon-btn round action-keys"
                onClick={() => setShowKeysModal(true)}
                title="API Keys (Google, OpenAI, Anthropic)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="7" cy="12" r="3"></circle>
                  <path d="M10 12h11"></path>
                  <path d="M18 12v3"></path>
                  <path d="M15 12v2"></path>
                </svg>
                <span className="sr-only">API Keys</span>
              </button>
            </div>
	            <div className={`lesson-viewport ${isFullscreen ? 'fullscreen' : ''}`}>
	              {pendingTemplateId && selectedTemplate === pendingTemplateId ? (
	                <div className="lesson-iframe pending" aria-label="Lesson loading"></div>
	              ) : (
	                <>
		                  <iframe
		                    ref={iframeRef}
		                    title="AI Lesson"
		                    src={buildIframeSrc(selectedTemplate, iframeNonce)}
		                    className="lesson-iframe"
		                  />
                  <LessonIframe 
                    iframeRef={iframeRef} 
                    lessonFrameRef={lessonFrameRef} 
                    setIsLessonLoading={setIsLessonLoading}
                    selectedTemplate={selectedTemplate}
                  />
                </>
              )}
            </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>
      
	      {/* Deploy Confirm Modal */}
	      {showDeployConfirmModal && (
	        <div className="modal-overlay" onClick={() => setShowDeployConfirmModal(false)}>
	          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
	            <div className="modal-header">
	              <h2>Deploy Lesson</h2>
	              <button className="modal-close" onClick={() => setShowDeployConfirmModal(false)}>
	                ×
	              </button>
	            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
                Deploy <strong>{availableTemplates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}</strong> to Cloudflare Pages?
              </p>
              {existingDeployment && (
                <div className="deployment-meta">
                  {existingDeployment.deployed_at && (
                    <div className="deployment-meta-row">
                      <span>Last deployed:</span>
                      <span>{formatDeployTime(existingDeployment.deployed_at)}</span>
                    </div>
                  )}
                  {existingDeployment.deployment_url && (
                    <a href={existingDeployment.deployment_url} target="_blank" rel="noreferrer">
                      {existingDeployment.deployment_url}
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={() => setShowDeployConfirmModal(false)}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={handleDeployConfirm}>
                Deploy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Status Modal */}
      {showDeployModal && (
        <div className="modal-overlay" onClick={() => { if (!isDeploying) setShowDeployModal(false); }}>
          <div className="modal-content deploy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cloudflare Deployment</h2>
              <button
                className="modal-close"
                onClick={() => { if (!isDeploying) setShowDeployModal(false); }}
                disabled={isDeploying}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
	              {isDeploying ? (
	                <div className="deployment-progress">
	                  <div className="deploy-spinner large"></div>
	                  <h3>Deploying...</h3>
	                  <p>Uploading your lesson to Cloudflare Pages.</p>
	                </div>
	              ) : deploymentStatus === 'success' && deploymentUrl ? (
	                <div className="deployment-success">
                  <div className="status-icon">✓</div>
	                  <h3>Deployment successful</h3>
	                  <p>Your lesson is live.</p>
	                  <a href={deploymentUrl} target="_blank" rel="noreferrer">
	                    Open deployment
	                  </a>
                  {existingDeployment?.production_url && existingDeployment.production_url !== deploymentUrl && (
                    <a href={existingDeployment.production_url} target="_blank" rel="noreferrer">
                      Open production URL
                    </a>
                  )}
                </div>
              ) : deploymentStatus === 'error' ? (
                <div className="deployment-error">
                  <div className="status-icon">!</div>
                  <h3>Deployment failed</h3>
                  <p>Please check your Cloudflare credentials and project settings.</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {showInlineAlert && (
        <div className="modal-overlay" onClick={() => setShowInlineAlert(false)}>
          <div className="modal-content inline-alert" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Notice</h2>
              <button className="modal-close" onClick={() => setShowInlineAlert(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                {inlineAlertMessage}
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-button primary" onClick={() => setShowInlineAlert(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Modal */}
      {showKeysModal && (
        <div className="modal-overlay" onClick={() => setShowKeysModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>API Keys</h2>
              <button className="modal-close" onClick={() => setShowKeysModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Add your keys to use your own API quotas. They stay on this device.
              </p>
              <div className="form-group">
                <label htmlFor="api-key-google">Google (Gemini)</label>
                <input
                  id="api-key-google"
                  type="password"
                  value={apiKeyInputs.google}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, google: e.target.value }))}
                  placeholder="AIza..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-openai">OpenAI (GPT)</label>
                <input
                  id="api-key-openai"
                  type="password"
                  value={apiKeyInputs.openai}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, openai: e.target.value }))}
                  placeholder="sk-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-anthropic">Anthropic (Claude)</label>
                <input
                  id="api-key-anthropic"
                  type="password"
                  value={apiKeyInputs.anthropic}
                  onChange={(e) => setApiKeyInputs(prev => ({ ...prev, anthropic: e.target.value }))}
                  placeholder="sk-ant-..."
                  className="form-input"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={() => setShowKeysModal(false)}>Cancel</button>
              <button className="modal-button primary" onClick={saveApiKeys}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Template Modal */}
      {showAddTemplateModal && (
            <div className="modal-overlay" onClick={() => {
              if (!isCreatingTemplate) {
                setShowAddTemplateModal(false);
                setNewTemplateModel(OPENAI_MODEL_ID);
                setNewTemplateImages([]);
                setNewTemplateImagePreviews([]);
              }
            }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Lesson</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  if (!isCreatingTemplate) {
                    setShowAddTemplateModal(false);
                    setNewTemplateModel(OPENAI_MODEL_ID);
                    setNewTemplateImages([]);
                    setNewTemplateImagePreviews([]);
                  }
                }}
                disabled={isCreatingTemplate}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
	              {isCreatingTemplate ? (
	                <div className="template-creating">
                  <div className="creating-spinner">
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                  </div>
	                  <h3>Creating Lesson...</h3>
	                  <p>AI is generating your lesson based on your description. This may take a moment.</p>
	                  <div className="creating-steps">
                    <div className="step-item">
                      <span className="step-icon">✓</span>
                      <span>Analyzing your requirements</span>
                    </div>
                    <div className="step-item">
                      <span className="step-icon">…</span>
                      <span>Generating HTML code</span>
                    </div>
	                    <div className="step-item">
	                      <span className="step-icon">…</span>
	                      <span>Creating lesson file</span>
	                    </div>
	                  </div>
	                </div>
	              ) : (
	                <>
	                  <div className="form-group">
	                  <label htmlFor="template-description">Vibe your lesson here</label>
	                  <textarea
	                    id="template-description"
	                    value={newTemplateDescription}
	                    onChange={(e) => setNewTemplateDescription(e.target.value)}
	                    placeholder="Describe the lesson you want (topic, level, objectives, interactions, tone)."
	                    disabled={isCreatingTemplate}
	                    className="form-textarea"
	                    rows={5}
	                  />
	                  </div>
                  <div className="form-group">
                    <label htmlFor="template-model">AI Model:</label>
                    <select
                      id="template-model"
                      value={enabledModelIds.includes(newTemplateModel) ? newTemplateModel : (enabledModelIds[0] || OPENAI_MODEL_ID)}
                      onChange={(e) => setNewTemplateModel(e.target.value)}
                      disabled={isCreatingTemplate}
                      className="form-input"
                      style={{ 
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--panel-bg)',
                        color: 'var(--text-primary)',
                        fontSize: '14px',
                        cursor: isCreatingTemplate ? 'not-allowed' : 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                    >
                      <option value="gemini" disabled={!isModelEnabled('gemini')}>Google Gemini 3 Flash Preview</option>
                      <option value={OPENAI_MODEL_ID} disabled={!isModelEnabled(OPENAI_MODEL_ID)}>GPT 5.1</option>
                      <option value="claude-sonnet-4-5" disabled={!isModelEnabled('claude-sonnet-4-5')}>Claude Sonnet 4.5</option>
                      <option value="claude-opus-4-6" disabled={!isModelEnabled('claude-opus-4-6')}>Claude Opus 4.6</option>
                    </select>
	                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
	                      Select which AI model to use for generating your lesson
	                    </small>
	                  </div>
                  <div className="form-group">
                    <label htmlFor="template-image">Reference Images (Optional, up to 6):</label>
                    <input
                      type="file"
                      id="template-image"
                      ref={templateImageInputRef}
                      accept="image/*"
                      multiple
                      onChange={handleTemplateImageSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateImageInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        🖼️ Choose Image(s)
                      </button>
                      {newTemplateImagePreviews.length > 0 && newTemplateImagePreviews.map((src, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img
                            src={src}
                            alt={`Preview ${idx + 1}`}
                            style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <button
                            type="button"
                            onClick={() => removeTemplateImage(idx)}
                            disabled={isCreatingTemplate}
                            style={{ position: 'absolute', top: '2px', right: '2px', padding: '2px 6px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload one or more images as design inspiration for the template
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="template-file">Data File (CSV/Excel/PDF/DOCX/PPTX) - Optional:</label>
                    <input
                      type="file"
                      id="template-file"
                      ref={templateFileInputRef}
                      accept=".csv,.xlsx,.xls,.pdf,.docx,.pptx"
                      onChange={handleTemplateFileSelect}
                      disabled={isCreatingTemplate}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => templateFileInputRef.current?.click()}
                        className="image-upload-button"
                        disabled={isCreatingTemplate}
                        style={{ padding: '8px 16px', cursor: 'pointer' }}
                      >
                        📁 Choose File
                      </button>
                      {newTemplateFile && (
                        <>
                          <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                            📄 {newTemplateFile.name}
                          </span>
                          <button
                            type="button"
                            onClick={removeTemplateFile}
                            disabled={isCreatingTemplate}
                            style={{ padding: '4px 8px', cursor: 'pointer' }}
                          >
                            × Remove
                          </button>
                        </>
                      )}
                    </div>
                    <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                      Upload a CSV, Excel, PDF, Word, or PowerPoint file to incorporate data into the template
                    </small>
                  </div>
	                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={() => {
                        setShowAddTemplateModal(false);
                        setNewTemplateModel(OPENAI_MODEL_ID);
                        setNewTemplateImages([]);
                        setNewTemplateImagePreviews([]);
                      }}
                      disabled={isCreatingTemplate}
                    >
                      Cancel
                    </button>
	                    <button
	                      className="modal-button primary"
	                      onClick={handleCreateTemplate}
	                      disabled={isCreatingTemplate || !newTemplateDescription.trim()}
	                    >
	                      Create lesson
	                    </button>
	                  </div>
	                </>
	              )}
            </div>
          </div>
        </div>
      )}

	      {/* Copy lesson Modal */}
	      {showCopyModal && (
        <div className="modal-overlay" onClick={() => {
          if (!isCopying) {
            setShowCopyModal(false);
            setCopyLessonName('');
          }
        }}>
	          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
	            <div className="modal-header">
	              <h2>Copy lesson</h2>
	              <button 
	                className="modal-close"
                onClick={() => {
                  if (!isCopying) {
                    setShowCopyModal(false);
                    setCopyLessonName('');
                  }
                }}
	                disabled={isCopying}
	              >
	                ×
	              </button>
	            </div>
	            <div className="modal-body">
	              <p style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
	                Create a copy of this lesson that you can edit. The original lesson will remain unchanged.
	              </p>
	              <div className="form-group">
	                <label htmlFor="copy-lesson-name">New Lesson Name:</label>
	                <input
	                  id="copy-lesson-name"
	                  type="text"
	                  value={copyLessonName}
	                  onChange={(e) => setCopyLessonName(e.target.value)}
	                  placeholder="Enter a name for the copied lesson"
	                  disabled={isCopying}
	                  className="form-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isCopying && copyLessonName.trim()) {
                      handleCopyLesson();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
	              <button
                className="modal-button cancel"
                onClick={() => {
                  setShowCopyModal(false);
                  setCopyLessonName('');
                }}
                disabled={isCopying}
              >
                Cancel
              </button>
              <button
                className="modal-button primary"
                onClick={handleCopyLesson}
	                disabled={isCopying || !copyLessonName.trim()}
	              >
	                {isCopying ? 'Copying...' : 'Copy lesson'}
	              </button>
	            </div>
	          </div>
	        </div>
	      )}

      {/* Code Preview Modal */}
      {showCodePreview && selectedTemplate && (
        <CodePreview
          templateId={selectedTemplate}
          isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
          onClose={() => setShowCodePreview(false)}
        />
      )}

	      {/* Delete Template Confirmation Modal */}
	      {showDeleteModal && templateToDelete && (
        <div className="modal-overlay" onClick={() => !isDeletingTemplate && handleDeleteCancel()}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
	            <div className="modal-header">
	              <h2>Delete lesson</h2>
	              <button
                className="modal-close"
                onClick={handleDeleteCancel}
                disabled={isDeletingTemplate}
                aria-disabled={isDeletingTemplate}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {deleteStep === 1 ? (
                <>
                  <div className="delete-warning">
                    <div className="warning-icon">!</div>
                    <h3>Are you sure?</h3>
                    <p>You are about to delete <strong>"{templateToDelete.name}"</strong>.</p>
                    <p>This action cannot be undone.</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                    >
                      Cancel
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                    >
                      Yes, Delete
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="delete-warning step2">
                    <div className="warning-icon">⚠️</div>
                    <h3>Final Confirmation</h3>
                    <p>This is your last chance to cancel.</p>
	                    <p>Deleting <strong>"{templateToDelete.name}"</strong> will permanently remove:</p>
	                    <ul className="delete-consequences">
	                      <li>The lesson file</li>
	                      <li>All associated chat history</li>
	                      <li>All saved data for this lesson</li>
	                    </ul>
                    <p className="final-warning">Are you absolutely sure?</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="modal-button cancel"
                      onClick={handleDeleteCancel}
                      disabled={isDeletingTemplate}
                    >
                      Cancel
                    </button>
                    <button
                      className="modal-button danger"
                      onClick={handleDeleteConfirm}
                      disabled={isDeletingTemplate}
                    >
                      {isDeletingTemplate ? (
                        <>
                          <span className="button-loading-dots" style={{ marginRight: '6px' }}>
                            <span></span><span></span><span></span>
                          </span>
                          Deleting...
                        </>
                      ) : (
                        'Delete Forever'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      </div> {/* main-pane */}
      {!isFullscreen && !chatMinimized && !chatDocked && !galleryViewMode && (
        <div
          className={`floating-chat ${chatMinimized ? 'minimized' : ''}`}
          style={{ left: 0, top: 0, transform: `translate(${chatPosition.x}px, ${chatPosition.y}px)` }}
        >
          <div
            className="floating-chat-header"
            onMouseDown={(e) => {
              chatDragRef.current.dragging = true;
              chatDragRef.current.offsetX = chatPosition.x - e.clientX;
              chatDragRef.current.offsetY = chatPosition.y - e.clientY;
            }}
          >
            <span className="floating-chat-title">Lesson Copilot</span>
            <div className="floating-chat-actions">
              <button
                className="icon-btn small"
                onClick={() => setChatMinimized(!chatMinimized)}
                title={chatMinimized ? 'Expand chat' : 'Minimize chat'}
              >
                <span aria-hidden>{chatMinimized ? '■' : '▁'}</span>
                <span className="sr-only">{chatMinimized ? 'Expand chat' : 'Minimize chat'}</span>
              </button>
              <button
                className="icon-btn small"
                onClick={() => {
                  setChatDocked(true);
                  setChatCollapsed(false);
                  setChatMinimized(true);
                }}
                title="Dock chat"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  <line x1="9" y1="3" x2="9" y2="21"></line>
                </svg>
                <span className="sr-only">Dock chat</span>
              </button>
            </div>
          </div>
          {!chatMinimized && (
            <div className="floating-chat-body">
              <div className="floating-chat-content">
                {selectedTemplate ? (
                  <ChatAdvisor
                    key={`chat-${selectedTemplate}`}
                    selectedTemplate={selectedTemplate}
                    isOwner={templateOwnership[selectedTemplate]?.is_owner ?? true}
                    isCreatingTemplate={isCreatingTemplate}
                    creatingTemplateDescription={creatingTemplateDescription}
                    pendingTemplateId={pendingTemplateId}
                    apiKeysConfig={apiKeysConfig}
                    defaultModelForTemplate={availableTemplates.find(t => t.id === selectedTemplate)?.default_model}
                    ownerId={getSessionId()}
                  />
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>It's empty</p>
                    <p>Select or create a lesson to chat with the copilot.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div> {/* layout-shell */}
    </div>
  );
}

export default App;
